<template>
  <div :class="['data-table', { 'data-table--selectable': selectable }]">
    <div v-if="reorderable || showPaginationInfo" class="data-table__reorder-bar">
      <div v-if="showPaginationInfo" class="data-table__pagination-info">
        <span
          v-if="selectedCount && selectedCount > 0"
          class="data-table__pagination-info__selected"
        >
          {{ selectedCount }} selected
        </span>
        <span v-else-if="pagination">
          Showing
          <span class="data-table__pagination-info__value">{{
            pagination.page * pagination.perPage <= pagination.total
              ? pagination.page * pagination.perPage
              : pagination.total
          }}</span>
          from
          <span class="data-table__pagination-info__value">{{ pagination.total }}</span>
          results
        </span>
      </div>
      <div class="data-table__reorder-bar-actions">
        <slot name="toolbar-actions" />
        <ColumnReorder
          v-if="reorderable"
          :columns="columnOrder"
          :sticky-columns="stickyColumns"
          @reorder="handleColumnReorder"
          @visibility-change="handleColumnVisibilityChange"
        />
      </div>
    </div>

    <div v-if="selectedIds.size > 0 && $slots['bulk-actions']" class="data-table__bulk-bar">
      <div class="data-table__bulk-bar-left">
        <input
          type="checkbox"
          :checked="allSelected"
          :indeterminate="someSelected && !allSelected"
          @change="(e) => handleSelectAll((e.target as HTMLInputElement).checked)"
          class="data-table__checkbox"
        />
        <span class="data-table__bulk-bar-count">{{ selectedIds.size }} selected</span>
      </div>
      <div class="data-table__bulk-bar-actions">
        <slot
          name="bulk-actions"
          :selectedIds="Array.from(selectedIds)"
          :count="selectedIds.size"
        />
      </div>
    </div>

    <div class="data-table__wrapper">
      <table class="data-table__table">
        <thead>
          <tr class="data-table__header-row">
            <th
              v-if="selectable"
              class="data-table__header-cell data-table__header-cell--checkbox data-table__header-cell--sticky"
            >
              <input
                type="checkbox"
                :checked="allSelected"
                :indeterminate="someSelected && !allSelected"
                @change="(e) => handleSelectAll((e.target as HTMLInputElement).checked)"
                class="data-table__checkbox"
              />
            </th>
            <th
              v-for="(column, colIndex) in orderedColumns"
              :key="String(column.key)"
              :class="[
                'data-table__header-cell',
                {
                  'data-table__header-cell--sticky-col': colIndex < stickyColumns,
                  'data-table__header-cell--sticky-col-last': colIndex === stickyColumns - 1,
                },
              ]"
              :style="{ ...getColumnStyle(column), ...getHeaderStickyStyle(colIndex) }"
            >
              <ColumnHeader
                :label="column.header"
                :sortable="column.sortable"
                :sort-direction="
                  sortState.column === String(column.key) ? sortState.direction : null
                "
                :filterable="column.filterable"
                :filter-type="column.filterType"
                :filter-options="column.filterOptions"
                :active-filters="filterState[String(column.key)] || []"
                @sort="(direction) => handleSort(String(column.key), direction)"
                @filter="(values) => handleFilter(String(column.key), values)"
              />
            </th>
            <th v-if="actions" class="data-table__header-cell data-table__header-cell--actions">
              <span class="sr-only">Actions</span>
            </th>
          </tr>
        </thead>
        <tbody class="data-table__body">
          <template v-if="loading">
            <tr v-for="n in 5" :key="'skeleton-' + n" class="data-table__row">
              <td v-if="selectable" class="data-table__cell data-table__cell--checkbox">
                <div class="data-table__skeleton" style="width: 16px; height: 16px"></div>
              </td>
              <td
                v-for="(column, colIndex) in orderedColumns"
                :key="String(column.key)"
                class="data-table__cell"
                :style="getColumnStyle(column)"
              >
                <div
                  class="data-table__skeleton"
                  :style="{ width: 60 + ((colIndex * 13) % 40) + '%' }"
                ></div>
              </td>
              <td v-if="actions" class="data-table__cell data-table__cell--actions">
                <div class="data-table__skeleton" style="width: 20px"></div>
              </td>
            </tr>
          </template>
          <tr v-else-if="isEmpty">
            <td :colspan="totalColumns" class="data-table__empty-cell">
              <TableEmptyState
                :icon="emptyState?.icon"
                :title="emptyState?.title"
                :description="emptyState?.description"
              >
                <template v-if="emptyState?.action" #action>
                  <component :is="emptyState.action" />
                </template>
              </TableEmptyState>
            </td>
          </tr>
          <tr
            v-for="(row, rowIndex) in filteredData"
            v-else
            :key="getRowKey(row, rowIndex)"
            :class="[
              'data-table__row',
              {
                'data-table__row--selected': isRowSelected(row),
              },
            ]"
          >
            <td
              v-if="selectable"
              class="data-table__cell data-table__cell--checkbox data-table__cell--sticky"
            >
              <Checkbox
                :model-value="isRowSelected(row)"
                @update:model-value="(val) => handleSelectRow(row, val)"
                size="sm"
              />
            </td>
            <td
              v-for="(column, colIndex) in orderedColumns"
              :key="String(column.key)"
              :class="[
                'data-table__cell',
                {
                  'data-table__cell--truncate': column.truncate !== false,
                  'data-table__cell--bold': column.bold,
                  'data-table__cell--sticky-col': colIndex < stickyColumns,
                  'data-table__cell--sticky-col-last': colIndex === stickyColumns - 1,
                },
              ]"
              :style="{ ...getColumnStyle(column), ...getStickyStyle(colIndex) }"
            >
              <slot
                :name="`cell(${String(column.key)})`"
                :row="row"
                :value="getCellValue(row, column.key)"
                :column="column"
              >
                <template v-if="column.render">
                  <RenderCell :render-fn="() => column.render!(row, rowIndex)" />
                </template>
                <template v-else-if="isEmptyValue(getCellValue(row, column.key))">
                  <span class="data-table__empty-value">
                    <span class="data-table__empty-dash"></span>
                  </span>
                </template>
                <template v-else-if="column.showTooltip">
                  <VTooltip>
                    <span class="data-table__cell-content">{{
                      String(getCellValue(row, column.key) ?? "")
                    }}</span>
                    <template #popper>
                      <div class="data-table__tooltip">
                        {{ String(getCellValue(row, column.key) ?? "") }}
                      </div>
                    </template>
                  </VTooltip>
                </template>
                <span v-else>{{ String(getCellValue(row, column.key)) }}</span>
              </slot>
            </td>
            <td v-if="actions" class="data-table__cell data-table__cell--actions">
              <ActionDropdown
                :row="row"
                :actions="typeof actions === 'function' ? actions(row) : actions"
              />
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-if="pagination && onPaginationChange" class="data-table__pagination-wrapper">
      <TablePagination
        :pagination="pagination"
        :per-page-options="perPageOptions"
        @page-change="(page) => onPaginationChange?.({ page })"
        @per-page-change="(perPage) => onPaginationChange?.({ perPage, page: 1 })"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed, watch, h, defineComponent } from "vue";
import { Icon } from "@iconify/vue";
import Checkbox from "@/components/ui/Checkbox.vue";
import ColumnHeader from "./ColumnHeader.vue";
import ColumnReorder from "./ColumnReorder.vue";
import TablePagination from "./TablePagination.vue";
import TableEmptyState from "./TableEmptyState.vue";
import ActionDropdown from "./ActionDropdown.vue";
import type {
  TableColumn,
  TableAction,
  PaginationState,
  ColumnState,
  SortState,
  FilterState,
  SortDirection,
} from "./types";

const RenderCell = defineComponent({
  props: {
    renderFn: {
      type: Function,
      required: true,
    },
  },
  setup(props) {
    return () => {
      const result = props.renderFn();
      if (result && typeof result === "object" && "type" in result) {
        return result;
      }
      if (typeof result === "string" || typeof result === "number") {
        return h("span", String(result));
      }
      return h("span", String(result ?? ""));
    };
  },
});

interface Props<T extends { id: string | number } = any> {
  data: T[];
  columns: TableColumn<T>[];
  actions?: TableAction<T>[] | ((row: T) => TableAction<T>[]);
  selectable?: boolean;
  onSelectionChange?: (selectedIds: (string | number)[]) => void;
  emptyState?: {
    title?: string;
    description?: string;
    action?: any;
    icon?: string;
  };
  pagination?: PaginationState;
  onPaginationChange?: (pagination: Partial<PaginationState>) => void;
  reorderable?: boolean;
  stickyColumns?: number;
  onSort?: (column: string, direction: SortDirection) => void;
  onFilter?: (filters: FilterState) => void;
  perPageOptions?: number[];
  showPaginationInfo?: boolean;
  selectedCount?: number;
  initialSelectedIds?: (string | number)[];
  loading?: boolean;
  storageKey?: string;
}

const props = withDefaults(defineProps<Props>(), {
  selectable: false,
  reorderable: false,
  stickyColumns: 1,
  perPageOptions: () => [50, 100, 200],
  showPaginationInfo: false,
  selectedCount: 0,
  initialSelectedIds: () => [],
  loading: false,
  storageKey: undefined,
});

const selectedIds = ref<Set<string | number>>(new Set());
const hasAppliedInitialSelection = ref(false);

function buildColumnOrder(): ColumnState[] {
  const defaults: ColumnState[] = props.columns.map((col) => ({
    key: String(col.key),
    header: col.header,
    visible: col.visible !== false,
  }));
  if (!props.storageKey) return defaults;
  try {
    const raw = localStorage.getItem(props.storageKey);
    if (!raw) return defaults;
    const saved: ColumnState[] = JSON.parse(raw);
    // Keep saved order/visibility; drop removed columns; append new ones
    const merged = saved
      .filter((s) => defaults.some((d) => d.key === s.key))
      .map((s) => {
        const current = defaults.find((d) => d.key === s.key)!;
        return { key: current.key, header: current.header, visible: s.visible };
      });
    defaults.forEach((d) => {
      if (!merged.some((m) => m.key === d.key)) merged.push(d);
    });
    return merged;
  } catch {
    return defaults;
  }
}

const columnOrder = ref<ColumnState[]>(buildColumnOrder());

watch(
  () => props.storageKey,
  () => {
    columnOrder.value = buildColumnOrder();
  }
);

watch(
  columnOrder,
  (newOrder) => {
    if (props.storageKey) {
      localStorage.setItem(props.storageKey, JSON.stringify(newOrder));
    }
  },
  { deep: true }
);
const sortState = ref<SortState>({ column: null, direction: null });
const filterState = ref<FilterState>({});

const allSelected = computed(() => {
  return (
    filteredData.value.length > 0 &&
    filteredData.value.every((row) => selectedIds.value.has(row.id))
  );
});

const someSelected = computed(() => {
  return filteredData.value.some((row) => selectedIds.value.has(row.id)) && !allSelected.value;
});

const orderedColumns = computed(() => {
  return columnOrder.value
    .filter((col) => col.visible)
    .map((col) => props.columns.find((c) => String(c.key) === col.key))
    .filter(Boolean) as TableColumn[];
});

const totalColumns = computed(() => {
  let count = orderedColumns.value.length;
  if (props.selectable) count++;
  if (props.actions && props.actions.length > 0) count++;
  return count;
});

const isEmpty = computed(() => filteredData.value.length === 0);

const getRowKey = (row: any, index: number): string | number => {
  return row.id ?? index;
};

const getCellValue = (row: any, key: string | number | symbol): any => {
  if (typeof key === "string" && key.includes(".")) {
    return key.split(".").reduce((obj, k) => obj?.[k], row);
  }
  return row[key];
};

const getColumnStyle = (column: TableColumn): Record<string, string> => {
  const style: Record<string, string> = {};
  if (column.width) style.width = column.width;
  if (column.maxWidth) style.maxWidth = column.maxWidth;
  if (column.minWidth) style.minWidth = column.minWidth;
  return style;
};

const getStickyStyle = (colIndex: number): Record<string, string> => {
  if (colIndex >= props.stickyColumns) return {};
  const checkboxOffset = props.selectable ? 36 : 0;
  let left = checkboxOffset;
  for (let i = 0; i < colIndex; i++) {
    const col = orderedColumns.value[i];
    if (col?.width) {
      left += parseInt(col.width, 10) || 0;
    } else if (col?.minWidth) {
      left += parseInt(col.minWidth, 10) || 0;
    } else {
      left += 150;
    }
  }
  return { position: "sticky", left: `${left}px`, zIndex: "2" };
};

const getHeaderStickyStyle = (colIndex: number): Record<string, string> => {
  const base = getStickyStyle(colIndex);
  if (!base.position) return {};
  return { ...base, top: "0px", zIndex: "4" };
};

const isEmptyValue = (value: any): boolean => {
  if (value === null || value === undefined) return true;
  if (typeof value === "string" && value.trim() === "") return true;
  if (Array.isArray(value) && value.length === 0) return true;
  return false;
};

const isRowSelected = (row: any): boolean => {
  return selectedIds.value.has(row.id);
};

const handleSelectAll = (checked: boolean) => {
  if (checked) {
    filteredData.value.forEach((row) => selectedIds.value.add(row.id));
  } else {
    filteredData.value.forEach((row) => selectedIds.value.delete(row.id));
  }
  props.onSelectionChange?.(Array.from(selectedIds.value));
};

const handleSelectRow = (row: any, checked: boolean) => {
  if (checked) {
    selectedIds.value.add(row.id);
  } else {
    selectedIds.value.delete(row.id);
  }
  props.onSelectionChange?.(Array.from(selectedIds.value));
};

const handleColumnReorder = (newOrder: ColumnState[]) => {
  columnOrder.value = newOrder;
};

const handleColumnVisibilityChange = (key: string, visible: boolean) => {
  columnOrder.value = columnOrder.value.map((col) => (col.key === key ? { ...col, visible } : col));
};

const handleSort = (columnKey: string, direction: SortDirection) => {
  sortState.value = { column: direction ? columnKey : null, direction };
  props.onSort?.(columnKey, direction);
};

const handleFilter = (columnKey: string, values: string[]) => {
  const newFilters = { ...filterState.value, [columnKey]: values };
  if (values.length === 0) {
    delete newFilters[columnKey];
  }
  filterState.value = newFilters;
  props.onFilter?.(newFilters);
};

const filteredData = computed(() => {
  let result = [...props.data];

  Object.entries(filterState.value).forEach(([columnKey, filterValues]) => {
    if (filterValues.length === 0) return;

    const column = props.columns.find((col) => String(col.key) === columnKey);
    if (!column) return;

    if (column.filterType === "multi-select" || !column.filterType) {
      result = result.filter((row) => {
        const value = getCellValue(row, column.key);

        if (Array.isArray(value)) {
          const arrayValues = value.map((item: any) => {
            if (item && typeof item === "object") {
              return String(item.id || item.tagId || item.value || item);
            }
            return String(item ?? "");
          });
          return filterValues.some((filterValue) =>
            arrayValues.some((arrVal) => String(arrVal).trim() === String(filterValue).trim())
          );
        }

        if (value && typeof value === "object") {
          const objId = value.id || value.tagId || value.value;
          if (objId !== undefined) {
            return filterValues.includes(String(objId));
          }
          const objStr = JSON.stringify(value);
          return filterValues.some((filterValue) => objStr.includes(String(filterValue)));
        }

        const strValue = String(value ?? "").trim();
        return filterValues.some((filterValue) => strValue === String(filterValue).trim());
      });
    }
  });

  if (sortState.value.column && sortState.value.direction) {
    const sortColumn = props.columns.find((col) => String(col.key) === sortState.value.column);
    if (sortColumn) {
      result.sort((a, b) => {
        const aValue = getCellValue(a, sortColumn.key);
        const bValue = getCellValue(b, sortColumn.key);
        const sortType = sortColumn.sortType || "alphabetic";

        let comparison = 0;

        if (sortType === "date") {
          const aDate = aValue ? new Date(aValue).getTime() : Number.NEGATIVE_INFINITY;
          const bDate = bValue ? new Date(bValue).getTime() : Number.NEGATIVE_INFINITY;
          const aValid = !isNaN(aDate) && isFinite(aDate);
          const bValid = !isNaN(bDate) && isFinite(bDate);

          if (!aValid && !bValid) comparison = 0;
          else if (!aValid) comparison = 1;
          else if (!bValid) comparison = -1;
          else comparison = aDate - bDate;
        } else if (sortType === "numeric") {
          const aNum = Number(aValue) || 0;
          const bNum = Number(bValue) || 0;
          comparison = aNum - bNum;
        } else {
          const aStr = String(aValue ?? "").toLowerCase();
          const bStr = String(bValue ?? "").toLowerCase();
          comparison = aStr.localeCompare(bStr);
        }

        return sortState.value.direction === "asc" ? comparison : -comparison;
      });
    }
  }

  return result;
});

watch(
  () => props.columns,
  (newColumns) => {
    const existingKeys = new Set(columnOrder.value.map((col) => col.key));
    const newKeys = new Set(newColumns.map((col) => String(col.key)));

    newColumns.forEach((col) => {
      const key = String(col.key);
      if (!existingKeys.has(key)) {
        columnOrder.value.push({
          key,
          header: col.header,
          visible: col.visible !== false,
        });
      } else {
        const existing = columnOrder.value.find((c) => c.key === key);
        if (existing) {
          existing.header = col.header;
        }
      }
    });

    columnOrder.value = columnOrder.value.filter((col) => newKeys.has(col.key));
  },
  { deep: true }
);

watch(
  () => props.data,
  () => {
    hasAppliedInitialSelection.value = false;
    // Sync selectedIds from initialSelectedIds to restore checkmarks on page navigation
    if (props.initialSelectedIds?.length) {
      const idsSet = new Set(props.initialSelectedIds.map(String));
      selectedIds.value = new Set(
        (props.data || [])
          .filter((row: any) => row.id != null && idsSet.has(String(row.id)))
          .map((row: any) => row.id)
      );
      hasAppliedInitialSelection.value = true;
    } else {
      selectedIds.value = new Set();
    }
  }
);

watch(
  [filteredData, () => props.initialSelectedIds],
  ([data, ids]) => {
    if (!data?.length || !ids?.length || hasAppliedInitialSelection.value || !props.selectable) {
      return;
    }
    const idsSet = new Set((ids || []).map((id) => String(id)));
    const toSelect: (string | number)[] = data
      .filter((row: any) => row.id != null && idsSet.has(String(row.id)))
      .map((row: any) => row.id);
    if (toSelect.length) {
      selectedIds.value = new Set(toSelect);
      props.onSelectionChange?.(Array.from(selectedIds.value));
      hasAppliedInitialSelection.value = true;
    }
  },
  { flush: "post", immediate: true }
);
</script>

<style scoped>
.data-table {
  background-color: #ffffff;
  border: 1px solid #eeeef2;
  border-radius: 12px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  height: 100%;
  max-height: 100%;
}

.data-table__reorder-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 4px 12px !important;
  border-bottom: 1px solid #eeeef2;
  background-color: #f9fafb;
  flex-shrink: 0;
}

.data-table__pagination-info {
  font-size: 12px;
  color: #9ca3af;
}

.data-table__reorder-bar-actions {
  display: flex;
  align-items: center;
  gap: 6px;
}

.data-table__pagination-info__value {
  font-weight: 500;
  color: #6b7280;
}

.data-table__pagination-info__selected {
  font-weight: 600;
  color: #3e3efe;
}

.data-table__wrapper {
  overflow-x: auto;
  overflow-y: auto;
  flex: 1;
  min-height: 0;
  width: 100%;
  position: relative;
}

.data-table__wrapper::-webkit-scrollbar {
  height: 8px;
  width: 8px;
}

.data-table__wrapper::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.data-table__wrapper::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 4px;
}

.data-table__wrapper::-webkit-scrollbar-thumb:hover {
  background: #a1a1a1;
}

.data-table__table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  table-layout: fixed;
}

.data-table__header-row {
  border-bottom: 1px solid #eeeef2;
  background-color: #f9fafb;
}

.data-table__header-cell {
  padding: 8px 12px !important;
  text-align: left;
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
  white-space: nowrap;
  position: sticky;
  top: 0;
  z-index: 3;
  background-color: #f9fafb;
}

.data-table__header-cell--checkbox {
  width: 36px;
  max-width: 36px;
  padding: 8px 4px 8px 12px !important;
}

/* Sticky column styles */
.data-table__header-cell--sticky {
  position: sticky;
  left: 0;
  top: 0;
  z-index: 4;
  background-color: inherit;
}

.data-table__cell--sticky {
  position: sticky;
  left: 0;
  z-index: 2;
  background-color: inherit;
}

.data-table__header-cell--sticky-col {
  position: sticky;
  top: 0;
  z-index: 4;
  background-color: inherit;
}

.data-table__cell--sticky-col {
  position: sticky;
  z-index: 2;
  background-color: inherit;
}

.data-table__cell--sticky-col-last::after,
.data-table__header-cell--sticky-col-last::after {
  content: "";
  position: absolute;
  top: 0;
  right: -4px;
  bottom: 0;
  width: 4px;
  background: linear-gradient(to right, rgba(0, 0, 0, 0.04), transparent);
  pointer-events: none;
}

.data-table__header-cell--actions {
  width: 40px;
  max-width: 40px;
  text-align: right;
  padding-right: 12px !important;
}

.data-table__body {
  background-color: #ffffff;
}

.data-table__row {
  border-bottom: 1px solid #eeeef2;
  transition: background-color 0.15s ease;
}

.data-table__row:hover {
  background-color: #f4f5f8;
}

.data-table__row--selected {
  background-color: #f8f7ff;
}

.data-table__row--selected:hover {
  background-color: #f0eeff;
}

.data-table__header-cell:first-child,
.data-table__cell:first-child {
  padding-left: 16px !important;
}

.data-table__cell {
  padding: 0 12px !important;
  font-size: 13px !important;
  font-weight: 500 !important;
  color: #1f2937 !important;
  text-align: left;
  vertical-align: middle;
}

.data-table__cell--truncate {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.data-table__cell--bold {
  font-weight: 600;
  color: #111827;
}

.data-table__cell-content {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.data-table__tooltip {
  max-width: 400px;
  word-break: break-word;
}

.data-table__empty-value {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
}

.data-table__empty-dash {
  display: block;
  width: 18px;
  height: 2px;
  border-radius: 1px;
  background: #d1d5db;
}

.data-table__cell :deep(.link) {
  text-decoration: none !important;
  color: #3e3efe;
  display: block;
  margin: -5px -12px;
  padding: 5px 12px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  transition: background-color 0.15s ease;
  cursor: pointer;
}

.data-table__cell :deep(.link:hover) {
  background-color: #f0eeff;
  color: #3e3efe;
  text-decoration: none !important;
}

.data-table__cell :deep(a) {
  text-decoration: none !important;
}

.data-table__cell :deep(a:hover) {
  text-decoration: none !important;
}

.data-table__cell :deep(.text-bold),
.data-table__cell :deep(.font-semibold) {
  font-weight: 600;
  color: #111827;
}

.data-table__cell :deep(.text-primary) {
  color: #3e3efe;
}

.data-table__cell--checkbox {
  width: 36px;
  max-width: 36px;
  padding: 0 4px 0 12px !important;
}

.data-table__cell--actions {
  width: 36px;
  max-width: 36px;
  text-align: right;
  padding-right: 12px !important;
}

.data-table__empty-cell {
  padding: 0;
  border: none;
}

.data-table__checkbox {
  width: 16px;
  height: 16px;
  cursor: pointer;
  accent-color: #3e3efe;
  border-radius: 4px;
  border: 1.5px solid #d0d5dd;
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border-width: 0;
}

.data-table__pagination-wrapper {
  flex-shrink: 0;
  background-color: #ffffff;
  border-top: 1px solid #eeeef2;
}

/* Bulk actions bar */
.data-table__bulk-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 12px;
  background-color: #eef0ff;
  border-bottom: 1px solid #d4d6ff;
  flex-shrink: 0;
}

.data-table__bulk-bar-left {
  display: flex;
  align-items: center;
  gap: 10px;
}

.data-table__bulk-bar-count {
  font-size: 13px;
  font-weight: 600;
  color: #3e3efe;
}

.data-table__bulk-bar-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

/* Row background colors for sticky cells */
.data-table__row .data-table__cell--sticky,
.data-table__row .data-table__cell--sticky-col {
  background-color: #ffffff;
}

.data-table__row:hover .data-table__cell--sticky,
.data-table__row:hover .data-table__cell--sticky-col {
  background-color: #f4f5f8;
}

.data-table__row--selected .data-table__cell--sticky,
.data-table__row--selected .data-table__cell--sticky-col {
  background-color: #f8f7ff;
}

.data-table__row--selected:hover .data-table__cell--sticky,
.data-table__row--selected:hover .data-table__cell--sticky-col {
  background-color: #f0eeff;
}

.data-table__skeleton {
  height: 14px;
  background: linear-gradient(90deg, #e5e7eb 25%, #f3f4f6 50%, #e5e7eb 75%);
  background-size: 200% 100%;
  animation: skeleton-shimmer 1.5s infinite;
  border-radius: 4px;
}

@keyframes skeleton-shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

.data-table__header-cell--sticky,
.data-table__header-cell--sticky-col {
  background-color: #f9fafb;
  top: 0;
  z-index: 4;
}
</style>
